import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Shopify Development & Store Management Services | Mad Marketing Club',
  description: 'Expert Shopify development services in Pune — custom store design, theme development, SEO, CRO, app integrations & D2C brand growth. Get a free consultation today.',
  canonical: 'https://madmarketingclub.com/services/shopify',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Shopify', url: 'https://madmarketingclub.com/services/shopify' },
  ],
  service: { name: 'Shopify Development & Store Management Services', type: 'Shopify Development' },
};

const OVERVIEW_POINTS = [
  'Easy-to-manage e-commerce platform',
  'Scalable for startups and enterprise brands',
  'Mobile-optimized storefronts',
  'Secure payment integrations',
  'SEO-friendly website structure',
  'Large app ecosystem',
  'Multi-channel selling support',
  'Inventory and order management tools',
  'High-converting checkout system',
  'Fast and reliable hosting infrastructure',
];

const APPROACH = [
  'Shopify store setup',
  'Shopify website design',
  'Custom theme development',
  'Shopify SEO',
  'Product catalog setup',
  'Conversion optimization',
  'App integrations',
  'Performance optimization',
  'Marketing support',
  'Store growth strategies',
];

const PROCESS = [
  {
    num: '01',
    title: 'Business Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Your brand',
      'Product categories',
      'Business model',
      'Target audience',
      'Competitor landscape',
      'Revenue goals',
      'Design preferences',
      'Growth objectives',
    ],
  },
  {
    num: '02',
    title: 'Shopify Store Audit & Planning',
    intro: 'We analyze and plan:',
    points: [
      'Store structure',
      'Customer journey',
      'Product organization',
      'Website UI/UX',
      'SEO opportunities',
      'Competitor benchmarking',
      'Technical requirements',
      'Conversion optimization strategy',
    ],
  },
  {
    num: '03',
    title: 'Store Design & Development Strategy',
    intro: 'Our customized roadmap includes:',
    points: [
      'Website wireframing',
      'Theme selection or custom design',
      'Mobile optimization',
      'SEO structure planning',
      'App integration planning',
      'Conversion optimization',
      'Performance optimization',
    ],
  },
  {
    num: '04',
    title: 'Development & Optimization',
    intro: 'We implement:',
    points: [
      'Shopify store setup',
      'Theme customization',
      'Product uploads',
      'Collection setup',
      'SEO optimization',
      'App integrations',
      'Payment gateway setup',
      'Shipping configuration',
      'Conversion enhancements',
    ],
  },
  {
    num: '05',
    title: 'Launch, Reporting & Scaling',
    intro: 'Continuous monitoring includes:',
    points: [
      'Website performance',
      'Traffic growth',
      'Conversion rates',
      'SEO rankings',
      'Customer behavior',
      'Sales growth',
      'Marketing performance',
      'Store optimization opportunities',
    ],
  },
];

const SERVICES = [
  {
    title: 'Shopify Store Setup & Development',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="9" y1="21" x2="9" y2="9" />
      </svg>
    ),
    points: [
      'Shopify store setup',
      'Theme installation',
      'Custom Shopify design',
      'Homepage development',
      'Collection page setup',
      'Product page optimization',
      'Navigation setup',
      'Mobile responsiveness',
      'Checkout optimization',
      'Custom functionality development',
    ],
  },
  {
    title: 'Shopify Theme Customization',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'Premium theme customization',
      'Custom section development',
      'Brand-focused UI/UX',
      'Mobile-first optimization',
      'Custom landing pages',
      'Visual merchandising',
      'User experience enhancement',
      'Store branding optimization',
    ],
  },
  {
    title: 'Shopify SEO Services',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'On-page SEO optimization',
      'Product SEO',
      'Collection page SEO',
      'Technical SEO',
      'Meta title & description optimization',
      'Site speed optimization',
      'Internal linking structure',
      'Schema markup implementation',
      'Image SEO optimization',
    ],
  },
  {
    title: 'Product Catalog Management',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product uploads',
      'Product description writing',
      'Variant setup',
      'Collection organization',
      'Product tagging',
      'Product merchandising',
      'Inventory setup',
      'Catalog optimization',
    ],
  },
  {
    title: 'Shopify App Integration Services',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'App installation',
      'CRM integrations',
      'Email marketing integrations',
      'Upsell & cross-sell tools',
      'Reviews & ratings integrations',
      'Inventory management systems',
      'Shipping integrations',
      'Analytics setup',
      'Automation workflows',
    ],
  },
  {
    title: 'Conversion Rate Optimization (CRO)',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Conversion funnel optimization',
      'Cart abandonment optimization',
      'Checkout flow enhancement',
      'Landing page optimization',
      'Product page optimization',
      'Mobile UX optimization',
      'Trust-building enhancements',
      'A/B testing consultation',
    ],
  },
  {
    title: 'Shopify Performance Optimization',
    gradient: 'linear-gradient(135deg, #0891b2, #06b6d4)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Website speed optimization',
      'Core Web Vitals optimization',
      'Image compression',
      'Lazy loading implementation',
      'Code optimization',
      'App performance auditing',
      'Mobile performance enhancement',
    ],
  },
  {
    title: 'Shopify Marketing & Growth Support',
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    points: [
      'Email marketing setup',
      'Abandoned cart automation',
      'Discount campaign setup',
      'Product launch campaigns',
      'Retargeting setup',
      'Customer retention strategies',
      'Upselling & cross-selling systems',
      'Sales funnel optimization',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Traffic analysis',
      'Conversion tracking',
      'Customer behavior analysis',
      'Product performance reports',
      'Marketing analytics',
      'Growth dashboards',
      'Profitability tracking',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated Shopify specialists',
  'SEO + Conversion optimization integration',
  'Mobile-first design approach',
  'High-performance storefront development',
  'Transparent reporting',
  'Scalable e-commerce strategies',
  'End-to-end Shopify management',
  'Long-term growth support',
];

const INDUSTRIES = [
  'Fashion & Apparel',
  'Beauty & Skincare',
  'Electronics',
  'Home & Kitchen',
  'Health & Wellness',
  'FMCG',
  'Jewelry Brands',
  'Pet Products',
  'D2C Consumer Brands',
  'Luxury Lifestyle Brands',
];

const STAGES = [
  'Launching a new online store',
  'Redesigning an existing Shopify store',
  'Scaling traffic and conversions',
  'Improving SEO and organic rankings',
  'Migrating from another platform',
  'Expanding to multi-channel commerce',
];

const ADDITIONAL = [
  'Shopify store migration (WooCommerce, Magento)',
  'Shopify Plus consulting',
  'Enterprise store optimization',
  'B2B commerce setup',
  'Multi-store management',
  'Social commerce setup',
  'Google Shopping integrations',
  'Omnichannel growth planning',
];

const FAQS = [
  {
    q: 'Do you develop complete Shopify stores?',
    a: 'Yes. We provide end-to-end Shopify store development including design, setup, SEO, product uploads, app integrations, and optimization.',
  },
  {
    q: 'Can you redesign existing Shopify stores?',
    a: 'Yes. We redesign Shopify stores to improve branding, user experience, mobile optimization, and conversion rates.',
  },
  {
    q: 'Do you provide Shopify SEO services?',
    a: 'Yes. We optimize Shopify stores for search engines through technical SEO, on-page optimization, speed optimization, and content improvements.',
  },
  {
    q: 'Can you integrate Shopify apps?',
    a: 'Yes. We integrate marketing, CRM, analytics, automation, inventory, shipping, and customer engagement apps based on your business needs.',
  },
  {
    q: 'Do you provide ongoing support and maintenance?',
    a: 'Yes. We provide continuous optimization, reporting, technical support, and growth management services for Shopify stores.',
  },
];

export default function ShopifyPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Shopify Store Development</span>
          </div>
        </nav>

        {/* HERO */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Shopify Store Development</div>
            <h1 className="sp-hero-h1">Shopify Store Development & Management Services</h1>
            <p className="sp-hero-sub">Complete Shopify Store Setup, Design, SEO & E-commerce Growth Solutions</p>
            <p className="sp-hero-desc">Build, launch, and scale your online business with professional Shopify store development and management services. From custom Shopify store setup and theme design to SEO optimization, conversion rate optimization, app integrations, and performance marketing support, we help brands create high-converting e-commerce stores designed for long-term growth.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">D2C</span><span className="sp-hero-stat-lbl">Store Specialists</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">SEO</span><span className="sp-hero-stat-lbl">& CRO Integrated</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Store Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">What is Shopify?</h2>
                <p className="sp-para">Shopify is one of the world's leading e-commerce platforms that enables businesses to create and manage online stores without requiring complex technical infrastructure. Shopify provides merchants with tools for website design, product management, payments, shipping, marketing, analytics, customer management, and multichannel selling through a scalable cloud-based ecosystem.</p>
                <p className="sp-para">Why Choose Shopify:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => (
                    <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>
                  ))}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Shopify marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End Shopify Store Development & Growth Management</h2>
            <p className="sp-para">Creating a successful Shopify store requires more than just launching a website. Long-term e-commerce growth depends on strong UI/UX design, SEO optimization, fast website performance, conversion-focused experiences, product merchandising, marketing integration, and operational efficiency.</p>
            <h3 className="sp-h3">We Work as Your Dedicated Shopify Growth Partner:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Shopify Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Shopify Services</div>
            <h2 className="sp-h2">E-commerce Experts Focused on Growth</h2>
            <p className="sp-para">We combine Shopify development, SEO, UI/UX optimization, conversion strategy, technical performance, and e-commerce growth expertise to help brands scale successfully online.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => (
                    <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>
                  ))}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Shopify store management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Shopify Growth Solutions for Every Business Stage</h2>
            <p className="sp-para">Whether you are:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured e-commerce growth strategy tailored to your business goals.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow Your Shopify Store?</h2>
            <p className="sp-cta-desc">Scale your e-commerce business with complete Shopify Store Development & Management Services focused on SEO, conversion optimization, UI/UX design, performance enhancement, and long-term online growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
